/**
 * DelegateMemberDialog — pick a pool member to hand off an approval claim.
 *
 * Replaces the v1 ``window.prompt('paste Cognito sub')`` affordance. The
 * delegate flow is intrinsically member-scoped (backend rejects non-pool
 * members with 400), so we hydrate from the same ``GET /pools/{id}/members``
 * the Pool Members drawer uses, filter out the current claimer (you
 * can't delegate to yourself), and surface a reason field for the
 * audit trail.
 *
 * Keyboard-first: typing filters, Enter on a highlighted row delegates,
 * Esc closes. No portal / modal library — one dialog, one job.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { listPoolMembers } from '@/services/poolMembersApi';

export default function DelegateMemberDialog({
  open,
  networkId,
  poolId,
  currentUserSub,
  onClose,
  onConfirm, // ({ toSub, toEmail, reason }) => Promise<void>
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedSub, setSelectedSub] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const inputRef = useRef(null);

  // Hydrate on open. We refetch every open because pool membership can
  // change out from under the room (co-creators add supervisors mid-shift).
  useEffect(() => {
    if (!open || !networkId || !poolId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelectedSub('');
    setQuery('');
    setReason('');
    setSubmitError(null);
    (async () => {
      try {
        const data = await listPoolMembers(networkId, poolId);
        if (cancelled) return;
        const list = Array.isArray(data?.members) ? data.members : [];
        setMembers(list);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load pool members');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, networkId, poolId]);

  // Focus the filter input when the dialog opens so typing narrows
  // straight away without reaching for the mouse.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = members
      // Can't delegate to yourself — server would 400, but hiding avoids
      // the "why doesn't this work?" foot-gun.
      .filter((m) => m.userSub && m.userSub !== currentUserSub);
    if (!q) return out;
    return out.filter((m) => {
      const hay = `${m.userName || ''} ${m.userSub || ''} ${m.email || ''} ${m.role || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [members, query, currentUserSub]);

  // Auto-select the first visible row so Enter has something to act on.
  useEffect(() => {
    if (!filtered.length) {
      setSelectedSub('');
      return;
    }
    if (!selectedSub || !filtered.some((m) => m.userSub === selectedSub)) {
      setSelectedSub(filtered[0].userSub);
    }
  }, [filtered, selectedSub]);

  if (!open) return null;

  const selectedMember = filtered.find((m) => m.userSub === selectedSub) || null;

  const submit = async () => {
    if (!selectedMember) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        toSub: selectedMember.userSub,
        toEmail: selectedMember.email || undefined,
        reason: reason.trim() || undefined,
      });
      onClose?.();
    } catch (e) {
      // Surface the server's own wording when we have it; otherwise a
      // generic message. ``data.message`` is the typed error body.
      setSubmitError(
        e?.data?.message || e?.message || 'Delegation failed',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    } else if (e.key === 'Enter' && selectedMember && !submitting) {
      e.preventDefault();
      submit();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!filtered.length) return;
      e.preventDefault();
      const idx = filtered.findIndex((m) => m.userSub === selectedSub);
      const next = e.key === 'ArrowDown'
        ? Math.min(filtered.length - 1, idx + 1)
        : Math.max(0, idx - 1);
      setSelectedSub(filtered[next]?.userSub || '');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        // Click outside the card closes. Clicks inside still propagate
        // because we stop in the inner div.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl"
        onKeyDown={handleKeyDown}
      >
        <div className="border-b border-gray-200 px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Delegate approval
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Hand this claim to another pool member. They'll immediately
            hold the claim and can resolve it.
          </p>
        </div>

        <div className="px-5 pt-4">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, email, or role…"
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        <div className="mt-3 max-h-64 overflow-y-auto border-y border-gray-100 bg-gray-50">
          {loading ? (
            <p className="px-5 py-6 text-sm text-gray-500">Loading members…</p>
          ) : loadError ? (
            <p className="px-5 py-6 text-sm text-red-600">{loadError}</p>
          ) : filtered.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500">
              {members.length === 0
                ? 'No other pool members yet — invite someone via Members on the network page.'
                : 'No members match that filter.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((m) => {
                const isSel = m.userSub === selectedSub;
                return (
                  <li
                    key={m.userSub}
                    // Button role gives us keyboard + mouse for free while
                    // keeping the visual treatment of a list row.
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedSub(m.userSub)}
                    onDoubleClick={() => {
                      setSelectedSub(m.userSub);
                      submit();
                    }}
                    className={`cursor-pointer px-5 py-2 text-sm ${
                      isSel
                        ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
                        : 'hover:bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-gray-900">
                          {m.userName || m.email || m.userSub}
                        </div>
                        {(m.userName || m.email) ? (
                          <div
                            className="truncate text-[11px] text-gray-500"
                            title={m.userSub}
                          >
                            {m.email || `${m.userSub.slice(0, 10)}…`}
                          </div>
                        ) : null}
                      </div>
                      <span
                        className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          (m.role || '').toUpperCase() === 'CREATOR'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {m.role || 'SUPERVISOR'}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 pt-3">
          <label className="block text-xs font-medium text-gray-700">
            Reason (optional, shown in audit trail)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Out of office — please cover"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        {submitError ? (
          <p className="px-5 pt-2 text-xs text-red-600">{submitError}</p>
        ) : null}

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!selectedMember || submitting}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Delegating…' : 'Delegate'}
          </button>
        </div>
      </div>
    </div>
  );
}
